import { useMemo } from 'react';

import { InputFieldSize } from '@xemplo/input-utils';
import { ListItem } from '@xemplo/listbox';

import { BaseTextDropdownMultiProps } from './text-dropdown-multi-v2.types';

type UseDropdownStyleProps<T extends ListItem> = BaseTextDropdownMultiProps<T> & {
  popoverIsOpen: boolean;
  listStateIsEmpty: boolean;
};

export function useDropdownStyle<T extends ListItem>(
  props: Readonly<UseDropdownStyleProps<T>>
) {
  const {
    items,
    invalid,
    isDropdownMulti,
    displayProps,
    selectProps,
    popoverIsOpen,
    listStateIsEmpty,
  } = props;

  const {
    leadingIcon,
    isReadOnly,
    isDisabled,
    inputSize = InputFieldSize.Standard,
  } = displayProps ?? {};

  const { label } = selectProps ?? {};

  const disableSingleSelect = useMemo(
    () => !isDropdownMulti && items.length === 1 && items[0].key === '-1',
    [isDropdownMulti, items]
  );

  const containerStyle = useMemo(() => {
    return {
      'input-has-error': !!invalid,
      'input-readonly': !!isReadOnly,
      'input-disabled': !!(isDisabled || disableSingleSelect),
      focused: popoverIsOpen,
    };
  }, [disableSingleSelect, isDisabled, isReadOnly, invalid, popoverIsOpen]);

  const placeholderStyle = useMemo(() => {
    return {
      'is-hidden': inputSize === InputFieldSize.Large && !popoverIsOpen,
      'has-leading-icon': !!leadingIcon,
      'has-label': !!(inputSize === InputFieldSize.Large && label),
      'input-readonly': !!isReadOnly,
    };
  }, [inputSize, popoverIsOpen, isReadOnly, leadingIcon, label]);

  const labelStyle = useMemo(() => {
    return {
      'move-up': popoverIsOpen || !listStateIsEmpty,
      'has-label': !!(inputSize === InputFieldSize.Large && label),
      'has-leading-icon': !!leadingIcon,
      'input-readonly': isReadOnly,
    };
  }, [inputSize, isReadOnly, leadingIcon, listStateIsEmpty, popoverIsOpen, label]);

  const selectStyle = useMemo(() => {
    return {
      'has-leading-icon': !!leadingIcon,
      'has-label': !!label,
    };
  }, [label, leadingIcon]);

  const arrowIconStyle = useMemo(() => {
    return {
      open: popoverIsOpen,
      'has-error': invalid,
    };
  }, [invalid, popoverIsOpen]);

  return { containerStyle, selectStyle, labelStyle, placeholderStyle, arrowIconStyle };
}
